import _extends from "@babel/runtime/helpers/extends";
import '../../css-tokens';
import React, { forwardRef, useRef, useEffect, useState } from 'react';
import cx from 'classnames';
import { Close } from '@mediahuis/chameleon-theme-wl/icons';
import Hidden from '../Hidden';
import Portal from '../Portal';
import Backdrop from '../Backdrop';
import { useFocusTrap, useBodyScrollLock } from '../../hooks';
import Flex from '../Flex';
import IconButton from '../IconButton';
import styles from './index.module.css';
const Dialog = /*#__PURE__*/function () {
  const Dialog = /*#__PURE__*/forwardRef((_ref, ref) => {
    let {
      children,
      show,
      onClose,
      closeOnEscape,
      closeOnBackdropClick,
      headerHidden,
      lockBodyOnScroll,
      accessibilityLabelCloseButton,
      footer,
      ...rest
    } = _ref;
    const wrapperRef = useRef(null);
    const innerContentRef = useRef(null);
    const [internalShow, setInternalShow] = useState(false);
    const [contentScroll, setContentScroll] = useState(false);
    const checkContentScroll = () => {
      // Needed for ios -> overflow scroll does not work when using max-height
      const HEADER_HEIGHT = 60;
      const OFFSET = 120;

      // Timeout needed for portal heigh
      // https://github.com/trendmicro-frontend/react-modal/issues/6
      setTimeout(() => {
        // compare height versus modal content
        if (window.innerHeight - HEADER_HEIGHT - OFFSET < (innerContentRef.current && innerContentRef.current.clientHeight ? innerContentRef.current.clientHeight : 1000)) {
          setContentScroll(true);
        } else {
          setContentScroll(false);
        }
      });
    };

    // Make sure onClose wont be triggered twice, use internalShow for everything.
    // Clicking iconButton now triggers the internal show, which will then trigger the onDeactivate of useFocusTrap
    useEffect(() => {
      setInternalShow(show);
      if (show) {
        checkContentScroll();
      }
    }, [show]);
    useFocusTrap(wrapperRef.current, internalShow, {
      clickOutsideDeactivates: closeOnBackdropClick,
      // we need to be able to click on the backdrop
      escapeDeactivates: closeOnEscape,
      returnFocusOnDeactivate: true,
      onDeactivate: onClose,
      fallbackFocus: wrapperRef.current
    });
    useBodyScrollLock(lockBodyOnScroll && internalShow);
    return /*#__PURE__*/React.createElement(Portal, {
      ref: ref
    }, /*#__PURE__*/React.createElement(Flex, _extends({}, rest, {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      className: cx(styles.wrap, !internalShow && styles.containerHidden, !!footer && styles.hasFooter),
      justifyContent: {
        xs: 'stretch',
        sm: 'center'
      },
      alignItems: {
        xs: 'stretch',
        sm: 'center'
      },
      flexDirection: {
        sm: 'column'
      }
    }), internalShow && /*#__PURE__*/React.createElement(Backdrop, {
      onClick: () => setInternalShow(false)
    }), /*#__PURE__*/React.createElement(Hidden, {
      screen: !internalShow,
      className: cx(styles.root, contentScroll && styles.rootContentScroll),
      role: "dialog",
      "aria-label": "dialog",
      tabIndex: "-1",
      ref: wrapperRef
    }, !headerHidden && /*#__PURE__*/React.createElement(Flex, {
      as: "header",
      justifyContent: "flex-end",
      className: styles.header
    }, /*#__PURE__*/React.createElement(IconButton, {
      icon: Close,
      circular: true,
      size: "small",
      onClick: () => setInternalShow(false),
      accessibilityLabel: accessibilityLabelCloseButton
    })), /*#__PURE__*/React.createElement("div", {
      className: cx(styles.content, headerHidden && styles.contentHeaderHidden)
    }, /*#__PURE__*/React.createElement("div", {
      ref: innerContentRef,
      className: styles.innerContent
    }, children)), !!footer && /*#__PURE__*/React.createElement(Flex, {
      as: "footer",
      alignItems: "center",
      justifyContent: {
        xs: 'space-between',
        sm: 'flex-end'
      },
      className: styles.footer
    }, footer))));
  });
  Dialog.displayName = 'Dialog';
  Dialog.identifier = Dialog.displayName;
  Dialog.defaultProps = {
    accessibilityLabelCloseButton: 'Close',
    closeOnBackdropClick: true,
    closeOnEscape: true,
    headerHidden: false,
    lockBodyOnScroll: true,
    show: false
  };
  return Dialog;
}();
export default Dialog;